<template>
  <div>
    <LeftSidebar
      :visible="visible"
      baseClass="filter-wrapper"
      :title="title"
      :hasBack="!!currentFilter"
      @back="backToFilters"
      @close="close"
    >
      <template #left-header-content>
        <span
          v-if="!currentFilter && hasFilter && !deleteLoading"
          class="filter-wrapper__reset-all"
          @click="cancelAll"
        >
          {{ $lang.cancelAll }}
        </span>
        <span
          v-if="!currentFilter && hasFilter && deleteLoading"
          class="d-flex color-primary"
        >
          <mdicon name="loading" size="24" class="spinner" />
        </span>
      </template>
      <template>
        <div v-if="currentFilter">
          <TextFilter
            v-if="currentFilter.type === 'text'"
            :item="currentFilter"
            :selectedFilter="selectedFilter"
            :multiple="true"
            @getFilterData="getFilterData"
          />
          <CheckboxFilter
            v-if="currentFilter.type === 'checkbox_multiselect'"
            :item="currentFilter"
            :selectedFilter="selectedFilter"
            :multiple="true"
            @getFilterData="getFilterData"
          />
          <CheckboxFilter
            v-if="currentFilter.type === 'checkbox_singleselect'"
            :item="currentFilter"
            :selectedFilter="selectedFilter"
            @getFilterData="getFilterData"
          />
          <CalenderFilter
            v-if="includes(currentFilter.type, ['datetime', 'date'])"
            :item="currentFilter"
            :selectedFilter="selectedFilter"
            @getFilterData="getFilterData"
          />
          <NumberFilter
            v-if="currentFilter.type === 'number'"
            :item="currentFilter"
            :selectedFilter="selectedFilter"
            @getFilterData="getFilterData"
          />
        </div>
        <div v-if="!currentFilter" class="padding-x-8 padding-y-16">
          <div
            class="filter-wrapper__item"
            v-for="(item, index) in items"
            :key="index"
            @click="setCurrentFilter(item)"
          >
            <span class="filter-wrapper__item-text">
              {{ item.caption }}
              <span v-if="item.tooltip" v-b-tooltip.hover :title="item.tooltip">
                <mdicon name="help-circle-outline" size="16" />
              </span>
            </span>
            <mdicon
              class="filter-wrapper__item-icon"
              name="chevron-left"
              size="24"
            />
          </div>
        </div>
      </template>
      <template #footer>
        <Button
          v-if="currentFilter"
          variant="primary"
          :text="$lang.submit_filter"
          class="mobile-width-percent-100 tablet-width-percent-100"
          contentClass="justify-content-center"
          :loading="loading"
          :disabled="isDisabledSubmit"
          @click="submit"
        />
      </template>
    </LeftSidebar>
  </div>
</template>

<script>
import { LeftSidebar } from "@/components/re-design";
import { Button } from "@/components/re-design/forms";
import TextFilter from "./TextFilter.vue";
import CheckboxFilter from "./CheckboxFilter.vue";
import CalenderFilter from "./CalenderFilter.vue";
import NumberFilter from "./NumberFilter.vue";
import { pathOr, includes } from "ramda";
import { toastAlert } from "@/shared/utils";

export default {
  components: {
    LeftSidebar,
    Button,
    TextFilter,
    CheckboxFilter,
    CalenderFilter,
    NumberFilter,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Array,
      default: () => [],
    },
    backdrop: {
      type: Boolean,
      default: false,
    },
    params: {
      type: [Object, Array],
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    exceptParams: {
      type: Array,
      default: () => [],
    },
    parentCurrentFilterKey: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      currentFilter: null,
      selectedFilters: {},
      deleteLoading: false,
      hasFilter: false,
      isDisabledSubmit: true,
    };
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          this.selectedFilters = { ...this.params };
          this.checkHasFilter();
        } else {
          this.backToFilters();
        }
      },
    },
    parentCurrentFilterKey: {
      immediate: true,
      handler(value) {
        if (value) {
          this.currentFilter = this.items.find(({ key }) => key === value);
        }
      },
    },
  },
  computed: {
    items() {
      return this.filters.map((item) => {
        return {
          caption: pathOr("", ["key_filter_caption"], item),
          tooltip: pathOr(null, ["search_scope"], item),
          key: pathOr(null, ["key_service"], item),
          name: pathOr(null, ["key_filter_name"], item),
          type: pathOr(null, ["type"], item),
          advanced: pathOr(false, ["advanced"], item),
          params: pathOr({}, ["params"], item),
          moduleName: pathOr(null, ["module"], item),
          isEnum: pathOr(false, ["is_enum"], item),
        };
      });
    },
    selectedFilter() {
      const key = pathOr(null, ["key"], this.currentFilter);
      const data = pathOr(null, [key], this.params);
      return Array.isArray(data) ? data : pathOr(data, ["value"], data);
    },
    title() {
      return pathOr(this.$lang.filter, ["caption"], this.currentFilter);
    },
  },
  methods: {
    pathOr,
    includes,
    close() {
      this.$emit("close");
    },
    setCurrentFilter(item) {
      this.currentFilter = item;
    },
    backToFilters() {
      this.currentFilter = null;
      this.isDisabledSubmit = true;
    },
    getFilterData(item, value) {
      const { key, type } = item;

      const checkboxCondition =
        type === "checkbox_multiselect" && !value.length;
      const numberCondition = type === "number" && !value;

      if (checkboxCondition || numberCondition) {
        delete this.selectedFilters[key];
        return;
      }

      this.selectedFilters[key] = {
        key,
        type,
        value,
      };
      this.isDisabledSubmit = false;
    },
    cancelAll() {
      this.deleteLoading = true;
      this.$emit("cancelAll", () => {
        this.selectedFilters = {};
        this.checkHasFilter();
        this.deleteLoading = false;
      });
    },
    submit() {
      let hasError = false;
      for (const [key, val] of Object.entries(this.selectedFilters)) {
        const actionType = pathOr(null, ["value", "actionType", "id"], val);
        const filterType = val.type;

        // check required set number
        const numberFieldKey = actionType === "between" ? "numOf" : "num";
        if (filterType === "number" && !val.value[numberFieldKey]) {
          hasError = true;
          return toastAlert("مقدار فیلتر عددی صحیح نمی باشد", "error");
        }

        // check required set date
        const dateFieldKey = actionType === "between" ? "startAt" : "date";
        if (
          includes(filterType, ["datetime", "date", "time"]) &&
          !val.value[dateFieldKey]
        ) {
          hasError = true;
          return toastAlert("مقدار فیلتر تاریخی صحیح نمی باشد", "error");
        }
      }

      if (!hasError) {
        this.$emit("submit", this.selectedFilters);
      }
    },
    checkHasFilter() {
      this.hasFilter = Object.keys(this.params).some(
        (key) => !includes(key, this.exceptParams)
      );
    },
  },
};
</script>
